// ─────────────────────────────────────────────────────────────────────────────
// HeyWaitr+ free-trial period helpers
//
// The free-trial length is not fixed: the backend advertises the current offer
// through GET /banners (e.g. "Unlock exclusive deal > Try 3 months for free"),
// which admins can change at any time. These helpers extract that period from
// the English banner text and localise it via translations.json.
// DEFAULT_TRIAL_PERIOD is only the pre-fetch / offline fallback.
// ─────────────────────────────────────────────────────────────────────────────
import translations from '../constants/translations.json';

// Shown until /banners resolves (or when it fails / carries no period).
export const DEFAULT_TRIAL_PERIOD = { count: 7, unit: 'day' };

// Monthly price (kr) shown until the live plan list resolves — matches the
// price on the backend's /subscription/getSubscriptionList monthly plan.
export const DEFAULT_MONTHLY_PRICE = 10;

// Banners that advertise the membership offer, most specific first.
const BANNER_PRIORITY = ['heywaitr+Banner', 'dashboardBanner'];

// "Try 3 months for free" / "7-day trial" / "1 week free" → { count, unit }.
const PERIOD_RE = /(\d+)[\s-]*(day|week|month|year)s?\b/i;

export const parseTrialPeriod = (text) => {
  if (typeof text !== 'string') return null;
  const match = text.match(PERIOD_RE);
  if (!match) return null;
  const count = parseInt(match[1], 10);
  if (!count) return null;
  return { count, unit: match[2].toLowerCase() };
};

// Scan the /banners payload for a trial period, preferring the banners that
// carry the membership offer over incidental matches elsewhere.
export const extractTrialPeriod = (banners) => {
  const list = Array.isArray(banners) ? banners : [];
  const ordered = [
    ...BANNER_PRIORITY.map((name) => list.find((b) => b?.Name === name)).filter(Boolean),
    ...list.filter((b) => !BANNER_PRIORITY.includes(b?.Name)),
  ];
  for (const banner of ordered) {
    const period = parseTrialPeriod(banner?.Text);
    if (period) return period;
  }
  return null;
};

// { count: 3, unit: 'month' } → "3 months" / "3 måneder" / "3 Monate" …
export const formatTrialPeriod = (period, language) => {
  const { count, unit } = period || DEFAULT_TRIAL_PERIOD;
  const key = `heywaitrplus_unit_${unit}${count === 1 ? '' : 's'}`;
  const word =
    translations[language]?.[key] || translations.en?.[key] || `${unit}${count === 1 ? '' : 's'}`;
  return `${count} ${word}`;
};

// Fill "{period}" / "{price}"-style placeholders in a translated string.
export const fillTemplate = (template, values) =>
  Object.entries(values || {}).reduce(
    (out, [name, value]) => out.split(`{${name}}`).join(String(value)),
    String(template ?? ''),
  );
